import { JSX } from "react";
import * as HeroIcons from "@heroicons/react/20/solid";
import { useTranslation } from "react-i18next";

import GetHeroIcon from "../../utils/getHeroIcons";
import { cn } from "../../utils/cn";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  label?: string | JSX.Element | JSX.Element[] | (() => JSX.Element);
  variant?: "outlined" | "default" | "contained";
  iconPosition?: "leading" | "trailing";
  iconName?: keyof typeof HeroIcons;
  iconOnly?: boolean;
  buttonColor?: "primary" | "secondary" | "warning" | "amber";
  rounded?: "md" | "lg" | "full";
  size?: "sm" | "md" | "lg" | "xl";
  disable?: boolean;
  testId?: string;
  iconVariant?: "solid" | "outline";
  isLoading?: boolean;
  loadingLabel?: string;
  loaderClass?: string;
  loadingLabelClass?: string;
}

const Button: React.FC<ButtonProps> = (props) => {
  const { t } = useTranslation();

  const {
    label,
    variant,
    iconPosition,
    iconName,
    iconOnly,
    rounded,
    size,
    disable = false,
    buttonColor,
    testId,
    iconVariant = "solid",
    className,
    children,
    isLoading,
    loadingLabel,
    loaderClass,
    loadingLabelClass,
    ...rest
  } = props;

  const buttonClasses = cn(
    variant &&
      "capitalize text-sm font-semibold ring-1 ring-inset px-3.5 py-2.5 ring-secondary/20 hover:bg-secondary/5",
    "disabled:bg-bkg",
    "disabled:border-border",
    "dark:disabled:bg-gray-50",
    "disabled:text-border",
    "disabled:ring-border",
    "disabled:hover:border-border",
    "disabled:hover:bg-bkg",
    "dark:disabled:bg-secondary-bg",
    "dark:disabled:text-secondary",
    size === "sm" && "px-2 py-1",
    size === "md" && "px-2.5 py-1.5",
    size === "lg" && "px-3 py-2",
    size === "xl" && "px-3.5 py-2.5",
    rounded && `rounded-${rounded}`,
    !rounded && "rounded-md",
    iconPosition &&
      iconPosition !== undefined &&
      "inline-flex items-center gap-x-2",
    variant === "contained" &&
      buttonColor === "primary" &&
      "bg-primary text-white shadow-sm hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary/90",
    variant === "contained" &&
      buttonColor === "secondary" &&
      "bg-secondary dark:bg-[#3b3b3b] dark:hover:bg-[#3b3b3b]/90 text-white shadow-sm hover:bg-secondary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary/90",
    variant === "contained" &&
      buttonColor === "amber" &&
      "bg-amber-400 text-white shadow-sm hover:bg-amber-400/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-400/90",
    variant === "contained" &&
      buttonColor === "warning" &&
      "bg-red-600 text-white shadow-sm hover:bg-red-600/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600/90",
    variant === "contained" &&
      !buttonColor &&
      "bg-primary text-white shadow-sm hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary/90",
    variant === "outlined" &&
      buttonColor === "primary" &&
      "bg-bkg text-primary/80 shadow-sm ring-1 ring-inset ring-primary/20 hover:bg-primary/5",
    variant === "outlined" &&
      buttonColor === "secondary" &&
      "bg-bkg text-secondary/80 shadow-sm ring-1 ring-inset ring-secondary/20 hover:bg-secondary/5",
    variant === "outlined" &&
      buttonColor === "amber" &&
      "bg-bkg text-amber-400 shadow-sm ring-1 ring-inset ring-secondary/20",

    variant === "outlined" &&
      !buttonColor &&
      "bg-bkg text-primary/80 shadow-sm ring-1 ring-inset ring-primary/20 hover:bg-primary/5",
    variant === "default" &&
      buttonColor === "secondary" &&
      "text-secondary/80 hover:rounded-md hover:shadow-sm hover:ring-1 hover:ring-inset ring-secondary/20 hover:bg-secondary/5",
    variant === "default" &&
      !buttonColor &&
      "text-primary/80 hover:rounded-md hover:shadow-sm hover:ring-1 hover:ring-inset ring-primary/20 hover:bg-primary-light",
    disable && "disabled",
    className
  );

  return (
    <>
      {isLoading && (
        <>
          <button
            type="button"
            className={buttonClasses}
            disabled={disable}
            data-testid={testId}
            {...rest}
          >
            <svg
              aria-hidden="true"
              className={cn(
                "h-4 aspect-square inline mr-2 text-gray-200 animate-spin dark:text-white fill-green-500",
                loaderClass
              )}
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
            <p className={cn(loadingLabelClass)}>
              {loadingLabel ?? t("common.loading")}
            </p>
          </button>
        </>
      )}
      {!isLoading && (
        <button
          type="button"
          className={buttonClasses}
          disabled={disable}
          data-testid={testId}
          {...rest}
        >
          {iconOnly && iconName && (
            <GetHeroIcon
              name={iconName}
              className="-mr-0.5 w-5 h-5 py-1"
              aria-hidden="true"
              variant={iconVariant}
            />
          )}
          {iconName && iconPosition !== "trailing" && (
            <>
              <GetHeroIcon
                name={iconName}
                className="-ml-0.5 w-5 h-5 py-0.5"
                aria-hidden="true"
                variant={iconVariant}
              />
              {label}
            </>
          )}
          {iconName && iconPosition === "trailing" && (
            <>
              {label}
              <GetHeroIcon
                name={iconName}
                className="-mr-0.5 w-5 h-5 py-0.5"
                aria-hidden="true"
                variant={iconVariant}
              />
            </>
          )}
          {!iconName && <>{label}</>}
          {children}
        </button>
      )}
    </>
  );
};

export default Button;
